import { Button, Card, Col, Form, Row } from "antd";
import FullPageLayout from "../../../templates/FullPageLayout";
import UploadPhoto from "../../../atoms/UploadPhoto";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { InputField, InputPhone, SelectField } from "../../../atoms/FormFields";
import { updateUserById } from "../../Team/ducks/services";
import { AxiosError } from "axios";
import { onImageDelete, uploadUserImage } from "../../MyProfile/ducks/services";
import { RcFile } from "antd/es/upload";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import {
  getCompanyFormData,
  getCompanyFormList,
  getUserDetailById,
  removeUserDetailData,
} from "../../Users/ducks/actions";

interface FormValues {
  first_name: string;
  last_name: string;
  firstLanguage: string;
  email: string;
  type: string;
  phone: string;
  phone_office: string;
  joined_date: string;
  birthday: string;
  companyId: string;
  position_id: string;
  service_line_id: string;
  rockstarAt: string;
}

export default function EditTeamMember() {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const url = useLocation();
  const id = url?.pathname.split("/")[2];

  const [loading, setLoading] = useState(false);
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const userDetailData = useAppSelector(
    (state) => state?.users?.userDetailData
  );
  // const companyFormData = useAppSelector(
  //   (state) => state?.users?.companyFormData
  // );
  const myProfileData = useAppSelector(
    (state) => state?.myProfile?.userData?.data
  );
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  const onSubmit = (val: FormValues) => {
    setLoading(true);
    const payload = {
      first_name: val.first_name,
      last_name: val.last_name,
      email: val.email,
      type: val.position_id,
      phone: val?.phone ? val?.phone : undefined,
      phone_office: val?.phone_office ? val?.phone_office : undefined,
      position_id: val.position_id,
    };

    updateUserById(id, payload)
      .then(() => {
        if (checklistFile && id) {
          uploadUserImage({
            owner_id: id,
            image: checklistFile as File | RcFile,
            image_type: "user_image",
          })
            .then((res) => {
              if (res) {
                setLoading(false);
                SuccessMessage("User updated");
                navigate("/admin-team");
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          SuccessMessage("User updated");
          navigate("/admin-team");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    dispatch(getCompanyFormList());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(getUserDetailById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(removeUserDetailData());
  }, [dispatch]);

  useEffect(() => {
    if (userDetailData?.data) {
      if (userDetailData?.data?.company_id) {
        dispatch(
          getCompanyFormData(userDetailData?.data?.company_id as string)
        );
      }

      setValue("first_name", userDetailData?.data?.first_name);
      setValue("last_name", userDetailData?.data?.last_name);
      setValue("email", userDetailData?.data?.email);
      setValue("firstLanguage", userDetailData?.data?.first_language);
      if (userDetailData?.data?.phone) {
        setValue("phone", userDetailData?.data?.phone);
      }
      if (userDetailData?.data?.phone_office) {
        setValue("phone_office", userDetailData?.data?.phone_office);
      }
      setValue("position_id", userDetailData?.data?.type);
    }
  }, [userDetailData, setValue, dispatch]);

  function callMembersAPI() {
    dispatch(getUserDetailById(id));
  }

  const roleOptions = [
    { label: "Super admin", value: "super_admin" },
    { label: "Client manager", value: "client_manager" },
  ];

  return (
    <FullPageLayout loading={userDetailData?.loader || loading}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Col className="addTeamMember">
          <div className="title">
            <h2> Edit team member </h2>
            <p>Make necessary changes in the form below.</p>
          </div>

          <Card>
            <Row gutter={20}>
              <Col span={24} style={{ marginBottom: "20px" }}>
                <h4 style={{ marginBottom: "16px" }}> Personal details </h4>
                <UploadPhoto
                  saveUpload={saveUpload}
                  loading={loading}
                  uploadModal={uploadModal}
                  imageUploaded={userDetailData?.data?.image_url}
                  setUploadModal={setUploadModal}
                  onImageDelete={() => {
                    onImageDelete(
                      setLoading,
                      SuccessMessage,
                      callMembersAPI,
                      ErrorMessage,
                      userDetailData?.data?.image_url
                        ? userDetailData?.data?.image_id
                        : undefined
                    );
                  }}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                  infoTextUploader="A photo helps others recognize this employee easily. It’s optional."
                  //   setIsCropModalOpen={setIsCropModalOpen}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldName="first_name"
                  label="First name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter first name",
                    autoComplete: "first_name",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "First name is required.",
                  }}
                  validate={errors.first_name && "error"}
                  validMessage={errors.first_name && errors.first_name.message}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="last_name"
                  label="Last name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter last name",
                    autoComplete: "last_name",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "Last name is required.",
                  }}
                  validate={errors.last_name && "error"}
                  validMessage={errors.last_name && errors.last_name.message}
                />
              </Col>
              <Col span={12}>
                <InputPhone
                  fieldName="phone"
                  label="Mobile phone"
                  control={control}
                  isRequired
                  rules={{
                    required: "Mobile phone is required.",
                  }}
                  validate={errors.phone && "error"}
                  validMessage={errors.phone && errors.phone.message}
                />
              </Col>
              <Col span={12}>
                <InputPhone
                  fieldName="phone_office"
                  label="Office phone"
                  control={control}
                />
              </Col>
              <Col span={24}>
                <InputField
                  fieldName="email"
                  label="Account email"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter account email",
                    autoComplete: "account_email",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "Account email is required.",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Please enter a valid email address.",
                    },
                  }}
                  validate={errors.email && "error"}
                  validMessage={errors.email && errors.email.message}
                />
              </Col>
              <Col span={24} className="mb-0">
                <SelectField
                  fieldName="position_id"
                  label="Role"
                  control={control}
                  selectOption={roleOptions}
                  iProps={{
                    placeholder: "Select role",
                    disabled:
                      myProfileData?.id === userDetailData?.data?.id
                        ? true
                        : false,
                  }}
                  isRequired
                  rules={{
                    required: "Role is required.",
                  }}
                  validate={errors.position_id && "error"}
                  validMessage={
                    errors.position_id && errors.position_id.message
                  }
                  hintMessage={
                    myProfileData?.id === userDetailData?.data?.id
                      ? "You can't change your role."
                      : ""
                  }
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() => navigate("/admin-team")}
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                {/* <Button
                  htmlType="submit"
                  className="outlined-btn"
                  type="primary"
                  loading={loading}
                >
                  Save & add one more
                </Button> */}
                <Button htmlType="submit" type="primary" className="mint-btn">
                  Save & close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
